import { useState, useCallback, useMemo } from 'react'
import { AxiosInstance } from 'axios'
import useAxios from 'api/useAxios'
import { IBaseView } from 'interfaces/navigationPage.interface'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export interface FilterItemT {
  id: string | number
  value?: string | number
  label: string
  disabled?: boolean
}

export interface ArticleFiltersT {
  diseases: FilterItemT[]
  orgs: FilterItemT[]
  candidates: FilterItemT[]
  pathogenTypes: FilterItemT[]
  techTypes: FilterItemT[]
  locations: FilterItemT[]
  tags: FilterItemT[]
}

export interface SelectedFiltersT {
  diseases: { label: string; value: string }[]
  locations: { label: string; value: string }[]
  orgs: { label: string; value: string }[]
  candidates: { label: string; value: string }[]
  pathogenTypes: { label: string; value: string }[]
  techTypes: { label: string; value: string }[]
  tags: { label: string; value: string }[]
}

export const DefaultArticleFilters = {
  diseases: [],
  locations: [],
  orgs: [],
  candidates: [],
  pathogenTypes: [],
  techTypes: [],
  tags: [],
}

type FixedFilterArg = [keyof SelectedFiltersT, string | number] | undefined

export function useArticleFilters(
  view: IBaseView<any, any>,
  fixedFilter?: FixedFilterArg
) {
  const axios: AxiosInstance = useAxios()
  const queryClient = useQueryClient()

  /** ---------- helpers ---------- */

  /** Ensure the fixed value exists in `arr` exactly once */
  const withFixed = useCallback(
    <T extends { value: string | number }>(
      arr: T[],
      key: keyof SelectedFiltersT
    ) => {
      if (!fixedFilter || fixedFilter[0] !== key) {
        return arr
      }

      const [_, fixedValue] = fixedFilter
      const isFixedValuePresent = arr.some((item) => item.value === fixedValue)

      if (isFixedValuePresent) {
        return arr
      }

      const fixedItem = {
        label: String(fixedValue),
        value: fixedValue,
      } as unknown as T
      return [...arr, fixedItem]
    },
    [fixedFilter]
  )

  /** ---------- selection state ---------- */

  const initialSelected = useMemo<SelectedFiltersT>(() => {
    const base = { ...DefaultArticleFilters }

    // inject the fixed filter once at boot
    if (fixedFilter) {
      const [k, v] = fixedFilter
      ;(base[k] as any) = withFixed(base[k] as any[], k)
    }
    return base
  }, [fixedFilter, withFixed])

  const [selected, setSelected] = useState<SelectedFiltersT>(initialSelected)

  /** guarded setter ─ cannot drop the fixed value */
  const updateFilter = useCallback(
    (type: keyof SelectedFiltersT, ids: any[]) => {
      setSelected((prev) => ({
        ...prev,
        [type]: withFixed(ids, type),
      }))
    },
    [withFixed]
  )

  /** ---------- query ---------- */

  const flatSelected = useMemo(() => {
    const pick = <K extends keyof SelectedFiltersT>(k: K) =>
      selected[k].map((i) => i.value)

    return {
      selectedDiseases: pick('diseases'),
      selectedOrgs: pick('orgs'),
      selectedLocations: pick('locations'),
      selectedCandidates: pick('candidates'),
      selectedPathogenTypes: pick('pathogenTypes'),
      selectedTechTypes: pick('techTypes'),
      selectedTags: pick('tags'),
    }
  }, [selected])

  const queryKey = [
    'article-filters',
    view.airtableBase ?? 'covid',
    view.airtableName,
    fixedFilter, // guarantees correct cache bucket
    flatSelected,
  ]

  const {
    data: options = DefaultArticleFilters,
    isFetching: isLoading,
    error,
    refetch,
  } = useQuery<ArticleFiltersT>({
    queryKey,
    queryFn: async () => {
      const airtableBase = view.airtableBase ?? 'covid'
      const { data } = await axios.post(
        `/${airtableBase}/rss-article-filters/?view=${view.airtableName}`,
        flatSelected
      )

      return {
        diseases: data.diseases ?? [],
        locations: data.locations ?? [],
        orgs: data.organisations ?? [],
        candidates: data.candidates ?? [],
        pathogenTypes: data.pathogenTypes ?? [],
        techTypes: data.techTypes ?? [],
        tags: data.tags ?? [],
      }
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  return {
    options,
    selected,
    updateFilter,
    isLoading,
    error,
    refetch,
    invalidate,
  }
}
